import React from 'react';
import { WorkSector } from '../../types/member';
import FormField from '../common/FormField';
import BilingualDoubleInput from './BilingualDoubleInput';
import PhoneWithDialCode from './PhoneWithDialCode';
import { FormRow, CheckboxContainer, Checkbox, CheckboxLabel } from '../../design-system/patterns/FormLayout';
import { Stack } from '../../design-system/components/Layout';

export interface WorkDetailsSectionProps {
  formData: {
    workSector?: string;
    professionalTitleEn?: string;
    professionalTitleGr?: string;
    professionalInstitutionNameEn?: string;
    professionalInstitutionNameGr?: string;
    professionalAddressEn?: string;
    professionalAddressGr?: string;
    professionalCityEn?: string;
    professionalCityGr?: string;
    professionalPostalCode?: string;
    professionalEmail?: string;
    professionalPhone?: string;
    website?: string;
    organisationNameEn?: string;
    organisationNameGr?: string;
    isGraduateStudent?: boolean;
    isRetired?: boolean;
  };
  setFormData: React.Dispatch<React.SetStateAction<any>>;
  t: (key: string, options?: Record<string, string>) => string;
  fieldErrors?: Record<string, string>;
  showCheckboxes?: boolean;
  /** Organisation is separate from the employing institution. */
  showOrganisationName?: boolean;
  onFieldBlur?: (fieldName: string) => void;
}

export const WorkDetailsSection: React.FC<WorkDetailsSectionProps> = ({
  formData,
  setFormData,
  t,
  fieldErrors = {},
  showCheckboxes = false,
  showOrganisationName = false,
  onFieldBlur,
}) => {
  return (
    <>
      <FormField
        label={t('register.workSector')}
        select
        options={[
          { value: '', label: t('register.selectWorkSector') },
          { value: String(WorkSector.Museology), label: t('workSector.museology') },
          { value: String(WorkSector.Education), label: t('workSector.education') },
          { value: String(WorkSector.Documentation), label: t('workSector.documentation') },
          { value: String(WorkSector.Conservation), label: t('workSector.conservation') },
          { value: String(WorkSector.Exhibition), label: t('workSector.exhibition') },
          { value: String(WorkSector.Other), label: t('workSector.other') },
        ]}
        value={formData.workSector ?? ''}
        onChange={(e) => setFormData((prev: any) => ({ ...prev, workSector: e.target.value }))}
      />
      <BilingualDoubleInput
        label={t('profile.professionalTitle')}
        valueEn={formData.professionalTitleEn ?? ''}
        valueGr={formData.professionalTitleGr ?? ''}
        onChangeEn={(v) =>
          setFormData((prev: any) => ({ ...prev, professionalTitleEn: v }))
        }
        onChangeGr={(v) =>
          setFormData((prev: any) => ({ ...prev, professionalTitleGr: v }))
        }
        placeholderEn="e.g. Museum Curator, Conservator"
        placeholderGr={t('register.professionalTitlePlaceholder')}
        greekFirst
      />
      <BilingualDoubleInput
        label={t('profile.professionalInstitution')}
        valueEn={formData.professionalInstitutionNameEn ?? ''}
        valueGr={formData.professionalInstitutionNameGr ?? ''}
        onChangeEn={(v) =>
          setFormData((prev: any) => ({ ...prev, professionalInstitutionNameEn: v }))
        }
        onChangeGr={(v) =>
          setFormData((prev: any) => ({ ...prev, professionalInstitutionNameGr: v }))
        }
        error={fieldErrors.professionalInstitution}
        placeholderEn="e.g. National Archaeological Museum"
        placeholderGr="π.χ. Εθνικό Αρχαιολογικό Μουσείο"
        greekFirst
      />
      {showOrganisationName && (
        <BilingualDoubleInput
          label={t('profile.organisationName')}
          valueEn={formData.organisationNameEn ?? ''}
          valueGr={formData.organisationNameGr ?? ''}
          onChangeEn={(v) =>
            setFormData((prev: any) => ({ ...prev, organisationNameEn: v }))
          }
          onChangeGr={(v) =>
            setFormData((prev: any) => ({ ...prev, organisationNameGr: v }))
          }
          shortLabels
        />
      )}
      <BilingualDoubleInput
        label={t('register.address')}
        valueEn={formData.professionalAddressEn ?? ''}
        valueGr={formData.professionalAddressGr ?? ''}
        onChangeEn={(v) =>
          setFormData((prev: any) => ({ ...prev, professionalAddressEn: v }))
        }
        onChangeGr={(v) =>
          setFormData((prev: any) => ({ ...prev, professionalAddressGr: v }))
        }
        greekFirst
      />
      <FormRow>
        <BilingualDoubleInput
          label={t('register.personalCity')}
          valueEn={formData.professionalCityEn ?? ''}
          valueGr={formData.professionalCityGr ?? ''}
          onChangeEn={(v) =>
            setFormData((prev: any) => ({ ...prev, professionalCityEn: v }))
          }
          onChangeGr={(v) =>
            setFormData((prev: any) => ({ ...prev, professionalCityGr: v }))
          }
          greekFirst
        />
        <FormField
          label={t('register.personalPostalCode')}
          value={formData.professionalPostalCode ?? ''}
          error={fieldErrors.professionalPostalCode}
          alignWithBilingual
          onChange={(e) =>
            setFormData((prev: any) => ({ ...prev, professionalPostalCode: e.target.value }))
          }
          onBlur={() => onFieldBlur?.('professionalPostalCode')}
        />
      </FormRow>
      <FormRow>
        <FormField
          label={t('register.professionalEmail')}
          type="email"
          name="professionalEmail"
          value={formData.professionalEmail ?? ''}
          error={fieldErrors.professionalEmail}
          onChange={(e) =>
            setFormData((prev: any) => ({ ...prev, professionalEmail: e.target.value }))
          }
          onBlur={() => onFieldBlur?.('professionalEmail')}
        />
        <PhoneWithDialCode
          id="register-professional-phone"
          name="professionalPhone"
          label={t('register.individual.telephoneWithCountryCode')}
          value={formData.professionalPhone ?? ''}
          error={fieldErrors.professionalPhone}
          onChange={(professionalPhone) =>
            setFormData((prev: any) => ({ ...prev, professionalPhone }))
          }
          onBlur={() => onFieldBlur?.('professionalPhone')}
        />
      </FormRow>
      <FormField
        id="register-workplace-website"
        name="website"
        label={t('register.workplaceWebsite')}
        type="text"
        inputMode="url"
        autoComplete="url"
        value={formData.website ?? ''}
        error={fieldErrors.website}
        onChange={(e) =>
          setFormData((prev: any) => ({ ...prev, website: e.target.value }))
        }
        onBlur={() => onFieldBlur?.('website')}
        placeholder="https://"
      />
      {showCheckboxes && (
        <Stack direction="column" spacing="md">
          <CheckboxContainer>
            <Checkbox
              type="checkbox"
              id="isGraduateStudent"
              checked={formData.isGraduateStudent ?? false}
              onChange={(e) => {
                const checked = e.target.checked;
                setFormData((prev: any) => ({
                  ...prev,
                  isGraduateStudent: checked,
                  // Keep category step in sync so the Studies wizard step appears/disappears.
                  professionalActivities: {
                    ...prev.professionalActivities,
                    studying: checked,
                  },
                }));
              }}
            />
            <CheckboxLabel htmlFor="isGraduateStudent">{t('profile.isGraduateStudent')}</CheckboxLabel>
          </CheckboxContainer>
          <CheckboxContainer>
            <Checkbox
              type="checkbox"
              id="isRetired"
              checked={formData.isRetired ?? false}
              onChange={(e) => {
                const checked = e.target.checked;
                setFormData((prev: any) => ({
                  ...prev,
                  isRetired: checked,
                  professionalActivities: {
                    ...prev.professionalActivities,
                    retired: checked,
                  },
                }));
              }}
            />
            <CheckboxLabel htmlFor="isRetired">{t('profile.isRetired')}</CheckboxLabel>
          </CheckboxContainer>
        </Stack>
      )}
    </>
  );
};

export default WorkDetailsSection;
